
import { computed, defineComponent, onMounted, ref, watch } from 'vue'
import {
  useOrganizationModules,
  useReportListing,
  useUser,
} from '@/shared/composables'
import { useRoute, useRouter } from 'vue-router'
import { getDateDistance, getDateFormat } from '@/shared/utils'
import {
  periodontalReportApi,
  reportApi,
  screeningReportApi,
} from '@/shared/api'
import { PeriodontalReportResponseType, ReportViewType } from '@/shared/types'
import { DATE_FORMAT, REPORT_VIEW } from '@/shared/constants'

export default defineComponent({
  name: 'PeriodontalReports',
  computed: {
    REPORT_VIEW() {
      return REPORT_VIEW
    },
  },

  setup() {
    const { user } = useUser()
    const { isReportListingUpdated, setUpdateReportListing } =
      useReportListing()

    const route = useRoute()
    const router = useRouter()

    const reports = ref<PeriodontalReportResponseType[]>([])
    const plaqueReportsLength = ref()
    const loading = ref(false)

    const patientId = computed(() => Number(route.params.patientId))
    const activeReportId = computed(() => Number(route.params.reportId))
    const isPreviewMode = computed(() => route.query['view-mode'] === '1')
    const isModalVisible = ref(false)
    const { organizationModules } = useOrganizationModules()
    const isSubscriptionEndedPeriodontal = computed(
      () =>
        !getDateDistance(user.value.organization.block_at) ||
        organizationModules.value.indexOf('periodontology') <= -1
    )
    const isSubscriptionEndedScreening = computed(
      () =>
        !getDateDistance(user.value.organization.block_at) ||
        organizationModules.value.indexOf('screening') <= -1
    )
    const isSubscriptionEnded = computed(
      () =>
        isSubscriptionEndedPeriodontal.value &&
        isSubscriptionEndedScreening.value
    )
    const transformReportsList = (array: [], isScreening: boolean) => {
      array.forEach((item: PeriodontalReportResponseType) => {
        item.isScreening = isScreening
      })

      return array
    }

    watch(patientId, (newPatientId, oldPatientId) => {
      if (newPatientId && newPatientId !== oldPatientId) {
        getReports()
      }
    })

    watch(
      () => isReportListingUpdated.value,
      (isUpdated) => {
        if (isUpdated) {
          getReports()
          setUpdateReportListing(false)
        }
      }
    )

    const openFirstReport = (): void => {
      const firstReportId = reports.value[0]?.id ?? 0
      const isScreening = reports.value[0]?.isScreening

      if (!firstReportId) return

      handleOpenReportPage(firstReportId, isScreening)
    }

    const getDate = (date: Date): string => {
      return getDateFormat(new Date(date), DATE_FORMAT.dmyLong)
    }

    const getReports = async (): Promise<void> => {
      loading.value = true

      reports.value = []

      const [errorPlaqueReport, responsePlaqueReport] =
        await reportApi.getReportList(patientId.value)

      const [errorPeriodontal, responsePeriodontal] =
        await periodontalReportApi.getReports(patientId.value)

      const [errorScreening, responseScreening] =
        await screeningReportApi.getReports(patientId.value)

      if (!errorPeriodontal && !errorScreening && !errorPlaqueReport) {
        plaqueReportsLength.value = responsePlaqueReport.data.reports.length
        reports.value = transformReportsList(
          responsePeriodontal.data.reports,
          false
        ).concat(transformReportsList(responseScreening.data.screenings, true))
      }

      if (
        !route.path.includes('screening') &&
        !route.path.includes('periodontal')
      ) {
        openFirstReport()
      }

      loading.value = false
    }

    const handleOpenReportCreationPage = (reportType: ReportViewType): void => {
      if (reportType === REPORT_VIEW.periodontal) {
        router.push({
          name: 'PeriodontalPage',
          params: { patientId: patientId.value, reportId: 0 },
        })
      } else {
        router.push({
          name: 'ScreeningReportPage',
          params: { patientId: patientId.value, reportId: 0 },
        })
      }

      isModalVisible.value = false
    }

    const handleOpenReportPage = async (
      reportId: number,
      isScreening: boolean
    ): Promise<void> => {
      if (isScreening) {
        if (route.query['view-mode'] === '1') {
          await router.push({
            name: 'ScreeningReportPage',
            params: { patientId: patientId.value, reportId },
            query: { 'view-mode': 1 },
          })
        } else {
          await router.push({
            name: 'ScreeningReportPage',
            params: { patientId: patientId.value, reportId },
          })
        }
      } else {
        if (route.query['view-mode'] === '1') {
          await router.push({
            name: 'PeriodontalPage',
            params: { patientId: patientId.value, reportId },
            query: { 'view-mode': 1 },
          })
        } else {
          await router.push({
            name: 'PeriodontalPage',
            params: { patientId: patientId.value, reportId },
          })
        }
      }
    }

    onMounted(async () => {
      await getReports()
    })

    return {
      reports,
      plaqueReportsLength,
      loading,
      activeReportId,
      isPreviewMode,
      isModalVisible,
      isSubscriptionEndedPeriodontal,
      isSubscriptionEndedScreening,
      isSubscriptionEnded,
      getDate,
      handleOpenReportCreationPage,
      handleOpenReportPage,
      getDateFormat,
      organizationModules,
    }
  },
})
